import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const STEPS = [
  {
    n: '1',
    title: 'Open Instagram Settings',
    body: 'On the mobile app, tap your profile picture → the menu icon (☰) → Settings and privacy. On desktop, click your profile picture → Settings.',
  },
  {
    n: '2',
    title: 'Find "Download your information"',
    body: 'In Settings, go to Account → Download your information (app) or Your activity → Download your information. On desktop it\'s under Privacy & Security.',
  },
  {
    n: '3',
    title: 'Select "Download or transfer information"',
    body: 'Choose "Some of your information" so you can select only what you need — this makes the download much smaller and faster.',
  },
  {
    n: '4',
    title: 'Choose "Connections"',
    body: 'Under the Connections category, check "Followers and following." You don\'t need anything else for Purgr.',
  },
  {
    n: '5',
    title: 'Select JSON format',
    body: 'When asked for a date range and format, choose "JSON" (not HTML). Set the date range to "All time." Both formats work with Purgr, but JSON is more reliable.',
  },
  {
    n: '6',
    title: 'Request the download',
    body: 'Tap "Create files." Instagram will send you an email when your download is ready — this typically takes anywhere from a few minutes to 48 hours depending on your account size.',
  },
  {
    n: '7',
    title: 'Download and unzip the file',
    body: 'Open the email from Instagram and click the download link. You\'ll receive a .zip file. Extract it to find your data files inside.',
  },
  {
    n: '8',
    title: 'Find following.json and followers_1.json',
    body: 'Inside the unzipped folder, look in the connections/followers_and_following/ directory. You need following.json and followers_1.json (Instagram may split followers across multiple files if you have a large following).',
  },
];

const HowToDownload: React.FC = () => {
  useSEO(
    'How to Download Your Instagram Data — Purgr',
    'Step-by-step guide to download your Instagram data export so you can see who follows you back. Takes about 5 minutes to request.'
  );

  return (
    <PageLayout title="How to Download Your Instagram Data">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">
        <p className="text-white/80 text-base">
          Before you can use Purgr to see who isn't following you back, you need to download your
          Instagram data. This is a built-in Instagram feature — no third-party access to your
          account is required. Here's how to do it.
        </p>

        {/* Steps */}
        <div className="space-y-4">
          {STEPS.map(({ n, title, body }) => (
            <div key={n} className="glass-card p-5 flex gap-4">
              <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                {n}
              </div>
              <div>
                <p className="text-white/90 font-semibold mb-1">{title}</p>
                <p className="text-white/55">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tips */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3">Tips</p>
          <ul className="space-y-2 text-white/55">
            <li>• Instagram limits how often you can request a data download — once every 24 hours.</li>
            <li>• Large accounts (10k+ followers) may take up to 48 hours to receive their download.</li>
            <li>• The files are only valid for 4 days after the download link is sent — download them promptly.</li>
            <li>• Your data files never leave your device when using Purgr — everything is processed locally in your browser.</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">Ready to check who's not following you back?</p>
            <p className="text-white/50 text-sm mb-4">Upload your files and get results in seconds — 100% private.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Try Purgr Free →
            </Link>
          </div>
        </div>

        {/* Related */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/how-to-see-who-doesnt-follow-you-back" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to see who doesn't follow you back →</p>
            </Link>
            <Link to="/how-to-clean-up-your-following-list" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to clean up your following list →</p>
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default HowToDownload;
